import { Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  EventEmitter,
  Output,
  ViewEncapsulation,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import mixinDisabled from '../../mixins/disabled';
import { isEmpty } from '../../utils';
import { PhxTabsetType } from './phx-tabset.type';
import { TabsetService } from './phx-tabset.service';

const baseMixin = mixinDisabled(
  class {
    constructor() {}
  }
);

@Component({
  selector: 'phx-tab',
  templateUrl: './phx-tab.component.html',
  styleUrls: ['./phx-tab.component.scss'],
  inputs: ['disabled'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'phx-tab',
    '[class.phx-tab-separator]': 'phxShowSeparator',
    '[class.phx-tab-hide-content]': 'phxHideContent',
    '[class.phx-tab-active]': 'isActive',
    '[class.phx-tab-disabled]': 'disabled',
    '[class.phx-tabset-primary]': 'phxTabType === "primary"',
    '[class.phx-tabset-secondary]': 'phxTabType === "secondary"',
  },
})
export class PhxTabComponent extends baseMixin implements OnInit, OnDestroy {
  @Input() public phxShowSeparator: boolean = false;
  @Input() public phxHideContent: boolean = false;
  @Input() public phxLink: string | string[];
  @Input() public phxDisableQueryParamMode: boolean = false;
  @Input() public phxDisableAutoSelect: boolean = false;
  @Output() public phxTabClick: EventEmitter<void> = new EventEmitter<void>();

  private _isActive: boolean = false;

  public get isActive(): boolean {
    return this._isActive;
  }

  public set isActive(value: boolean) {
    this._isActive = value;
    this._cdr.detectChanges();
  }
  public oldTabBoxWidth: number;
  public phxTabType: PhxTabsetType =  "secondary";
  private $destroy: Subject<void> = new Subject<void>();
  private _routerSubscription: Subscription;

  @ViewChild('tabBox') public tabBox: ElementRef;

  constructor(private _tabsetService: TabsetService, private _router: Router, private _cdr: ChangeDetectorRef) {
    super();
  }

  public ngOnInit(): void {
    this._tabsetService.tabChanged$.pipe(
      takeUntil(this.$destroy)
     ).subscribe((tab: PhxTabComponent) => {
      if (tab) {
        this.isActive = this === tab;
        this._cdr.detectChanges();
      }
    });
    this.checkAndChangeRouteOnUrlChange();
    this._tabsetService.tabTypeChanged$.pipe(
      takeUntil(this.$destroy)
    ).subscribe((type: PhxTabsetType) => {
      this.phxTabType = type;
      this._cdr.detectChanges();
    });

    if(this._tabsetService.phxMatchUrl && !this._routerSubscription && !this.isEmptyCheck(this.phxLink)) {
      this._routerSubscription = this._router.events.pipe(
        filter(event => event instanceof NavigationEnd),
      ).subscribe(() => {
        this.checkAndChangeRouteOnUrlChange();
      });
    }
  }

  public ngOnChanges(simpleChanges: SimpleChanges): void {
    const { phxLink } = simpleChanges;
    if(this._tabsetService.phxMatchUrl && phxLink) {
      this.checkAndChangeRouteOnUrlChange();
    }
  }

  private checkAndChangeRouteOnUrlChange(): void {
    // Skip URL-based activation/deactivation if the tab has no link
    if (this.isEmptyCheck(this.phxLink)) {
      return;
    }
    
    const oldActive = this.isActive;
    this.isActive = this.matchUrl();
    if (oldActive !== this.isActive && this.isActive) {
      this._tabsetService.onTabChanged(this);
    }
  }

  public ngOnDestroy(): void {
    this.$destroy.next();
    this.$destroy.complete();
    this._routerSubscription?.unsubscribe();
  }

  public onTabClick(event: MouseEvent): void {
    if (!this.disabled) {
      this.phxTabClick.emit();
      if (!this.phxDisableAutoSelect && !this.isModifiedTabClick(event)) {
        this._tabsetService.onTabChanged(this);
      }
    }
  }

  public onContentChange(): void {
    this._tabsetService.onChangeIndicatorPosition();
  }

  public isEmptyCheck(val: string | string[]): boolean {
    return isEmpty(val);
  }

  public matchUrl(): boolean {
    const urlWithoutParams = this.removeQueryParameters(this._router.url);
    return this.matchLink(urlWithoutParams, this.phxLink);
  }

  private matchLink(url: string, link: string | string[]): boolean {
    if (Array.isArray(link)) {
      const linkString = link.filter(item => typeof item !== 'object').join('/');
      return this._tabsetService.phxShouldMatchExactLink ? url === linkString : url.includes(linkString);
    } else {
      return this._tabsetService.phxShouldMatchExactLink ? url === link : url.includes(link);
    }
  }

  private removeQueryParameters(url: string): string {
    const queryIndex = url.indexOf('?');
    const semicolonIndex = url.indexOf(';');

    // It will convert "example.com?param1=1&param2=2" to "example.com"
    if (queryIndex !== -1) {
      return url.substring(0, queryIndex);
    }

    // It will convert "example.com;param1=1;param2=2" to "example.com"
    if (semicolonIndex !== -1) {
      return url.substring(0, semicolonIndex);
    }

    return url;
  }

  private isModifiedTabClick(event: MouseEvent): boolean {
    return !!event && (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey);
  }
}
